import {
    BootFirmware,
    DocumentType,
    type BillingAccount,
    type CursorPagingInfo,
    type GetBillingAccountByIdQuery,
    type GetBillingAccountListQuery,
    type GetBucketListQuery,
    type GetInvoiceListQuery,
    type GetIpAddressListQuery,
    type GetLoadBalancerListQuery,
    type GetServicePackageListQuery,
    type GetUserDetailsQuery,
    type GetUserListQuery,
    type GetVmListQuery,
    type PagingInfo,
    type RunningTotals,
} from "@/admin/graphql.gen/graphql.ts";
import { defaultHostUuid } from "@/mock-api/data/adminHostExample.ts";
import { extremeHostPoolUuid } from "@/mock-api/data/adminHostPoolExample.ts";
import {
    postPaymentClear,
    prePaymentClear,
    prePaymentPending,
    prePaymentRecurring,
} from "@/mock-api/data/billingAccountExample.ts";
import { vmImagesExample } from "@/mock-api/data/vmImagesExample.ts";
import { mustFindBy } from "@/mock-api/mustFindBy.ts";
import {
    randomElement,
    randomMac,
    randomPastTimestamp,
    randomPrivateIpv4,
    randomPublicIpv4,
    randomPublicIpv6,
    robin,
} from "@/mock-api/randomTypes.ts";
import { generateUuid } from "@/mock-api/requestUtils.ts";
import { withGlobalSeed } from "@/utils/random.ts";
import type { BillingAccount as RestBillingAccount } from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";
import { pick, times } from "remeda";
import type { PartialDeep } from "type-fest";

//
// CODE GUIDELINES:
//
// - For the mock data, must use either:
//  - Common constants, eg.  `billing_account_object` constant where possible
//  - Dynamically generated data with `withGlobalSeed` and the `random*` functions
//

//#region Shared sub-objects

const billing_account_id = 129554;
const user_id = 465;

// NB: These can include _more_ fields than the a single query requires, ie. these can contain _all_ the fields used by _any_ query.

const running_totals = {
    subtotal: 0,
    ongoing_negative_since: null,
    ongoing: 1234,
    discount_amount: 0,
    __typename: "RunningTotals",
} satisfies RunningTotals;

const billing_account_object = {
    id: String(billing_account_id),
    allow_debt: true,
    title: "Demo Billing Account",
    email: "demo@warren.io",
    user_id,
    __typename: "BillingAccount",
    running_totals,
} satisfies Partial<BillingAccount>;

const prepaymentBillingAccount = {
    id: "130111",
    allow_debt: false,
    title: "Pre-payment",
    email: "prepay@warren.io",
    user_id,
    __typename: "BillingAccount",
} satisfies Partial<BillingAccount>;

const cursorPaging = {
    total: 50,
    cursor: null,
    __typename: "CursorPagingInfo",
} satisfies CursorPagingInfo;

const regularPaging = {
    total: 50,
    __typename: "PagingInfo",
} satisfies PagingInfo;

//#endregion

const commonAccountFields = {
    customer_name: "",
    customer_phone: null,
    customer_id_number: null,
    allow_debt: false,
    is_recurring_payment_enabled: false,
    send_invoice_email: true,
    discount_percentage: 0,
    suspend_reason: "",
    referral_code: null,
    paying_by_invoice: false,
    integration_id: null,
    primary_card: null,
    running_totals,
    company_reg_code: null,
    company_vat_number: null,
    // Fields common to at least 2 objects
    credit_amount: 0,
    precalc_credit_amount: 0,
    precalc_days_in_debt: 0,
    precalc_ongoing: 0,
    country: "EE",
    reseller: "Pilw",
    vat_percentage: 24,
    additional_data: null,
    is_deleted: false,
    deleted_at: null,
    kyc_info: {
        provider: "digio",
        __typename: "CleanKycInfo",
    },
    __typename: "BillingAccount" as const,
} satisfies PartialDeep<BillingAccount>;

type BillingAccountListItem = NonNullable<
    NonNullable<GetBillingAccountListQuery["admin_billing_account_list"]["items"]>[number]
>;

/** Map a user-side REST billing account to a GraphQL admin list item */
function mapUserBillingAccount({
    title,
    email,
    id,
    company_name,
    is_active,
    restriction_level,
    status,
    created,
    referral_share_code,
    county,
    city,
    address_line1,
    post_index,
}: RestBillingAccount): BillingAccountListItem {
    return {
        ...commonAccountFields,
        title,
        email,
        id: String(id),
        user_id,
        company_name,
        is_active,
        restriction_level,
        status,
        created,
        referral_share_code,
        kyc_info: null,
        county,
        city,
        address_line1,
        post_index,
    };
}

export const getBillingAccountListQueryExample: GetBillingAccountListQuery = {
    admin_billing_account_list: {
        items: [
            {
                ...commonAccountFields,
                ...billing_account_object,
                user_id,
                company_name: "Company",
                is_active: true,
                allow_debt: true,
                restriction_level: "CLEAR",
                status: "ACTIVE",
                created: 1764140149,
                referral_share_code: "fr67jh",
                paying_by_invoice: true,
                primary_card: {
                    card_holder: "demo@warren.io",
                    card_type: "By invoice",
                    expire_month: 12,
                    expire_year: 2099,
                    id: "N/A",
                    is_verified: true,
                    is_verification_pending: false,
                    last4: null,
                    type: "paying_by_invoice",
                    __typename: "PrimaryCard",
                },
                kyc_info: null,
                county: "United States",
                city: "New York",
                address_line1: "Address 12",
                post_index: "12345",
            },
            {
                ...commonAccountFields,
                title: "Digio reject",
                email: "demo+rejectKYC@warren.io",
                id: "131825",
                user_id,
                company_name: "Demo Company",
                is_active: false,
                restriction_level: "TERMINATED",
                status: "PENDING_VALIDATION",
                created: 1764854911,
                referral_share_code: "h2jt6f",
                is_deleted: true,
                deleted_at: 1764854998,
                running_totals: {
                    ...running_totals,
                    ongoing: -123,
                },
                kyc_info: {
                    ...commonAccountFields.kyc_info,
                    status: "rejected",
                },
                county: "Harju County",
                city: "Tallinn",
                address_line1: "Pärnu maantee 123",
                post_index: "10112",
                // cspell:words Harju Pärnu maantee
            },
            {
                ...commonAccountFields,
                ...prepaymentBillingAccount,
                reseller: "idROOT",
                company_name: "Nordic Tech Solutions",
                is_active: true,
                restriction_level: "LIMITED",
                vat_percentage: 10,
                status: "ACTIVE",
                credit_amount: 17,
                precalc_credit_amount: 17,
                precalc_ongoing: 17,
                created: 1764769432,
                running_totals: {
                    ...running_totals,
                    ongoing: 0,
                },
                referral_share_code: "tx2fw7",
                additional_data: '{"link_methods": ["razorpay::razorpay"]}',
                kyc_info: {
                    ...commonAccountFields.kyc_info,
                    status: "approved",
                },
                country: "FI",
                county: "Uusimaa",
                city: "Helsinki",
                address_line1: "Mannerheimintie 45",
                post_index: "00100",
                // cspell:words Uusimaa Mannerheimintie
            },
            {
                ...commonAccountFields,
                title: "My Billing Account",
                email: "admin@warren.io",
                id: "131796",
                user_id,
                company_name: "Company OÜ",
                is_active: false,
                restriction_level: "FROZEN",
                status: "PENDING_VALIDATION",
                created: 1757337719,
                referral_share_code: "s9c67w",
                kyc_info: {
                    ...commonAccountFields.kyc_info,
                    status: "started",
                },
                county: "Some State",
                city: "City Name",
                address_line1: "Street no 1",
                post_index: "123456",
            },
            // Shared with the REST mock DB, so `BillingAccountById` resolves IDs used by eg. custom products and metal machines.
            ...[postPaymentClear, prePaymentClear, prePaymentRecurring, prePaymentPending].map(mapUserBillingAccount),
        ],
        paging: regularPaging,
        __typename: "BillingAccountList",
    },
};

export function getBillingAccountByIdExample(baId: string): GetBillingAccountByIdQuery {
    const { items } = getBillingAccountListQueryExample.admin_billing_account_list;
    const { title, email, id } = mustFindBy(notNull(items), (item) => notNull(item).id, baId, "billing account");
    return {
        billing_account_details: {
            title,
            email,
            id,
            __typename: "BillingAccount",
        },
    };
}

export const getUserDetailsQueryExample: GetUserDetailsQuery = {
    admin_user_details: {
        id: "328",
        signup_site: "Demo",
        name: "demo@warren.io",
        profile_data: {
            created_at: "2022-01-19 13:52:49.029680",
            email: "demo@warren.io",
            first_name: "John",
            last_name: "Smith",
            __typename: "UserProfileData",
        },
        __typename: "User",
    },
};

export const getUserListQueryExample: GetUserListQuery = {
    admin_user_list: {
        items: [
            {
                id: String(user_id),
                name: "user@warren.io",
                profile_data: {
                    created_at: "2025-11-26 06:55:15.736380",
                    email: "user@warren.io",
                    first_name: null,
                    last_name: null,
                    __typename: "UserProfileData",
                },
                __typename: "User",
            },
            {
                id: "464",
                name: "admin@warren.io",
                profile_data: {
                    created_at: "2025-09-05 11:14:35.688251",
                    email: "admin@warren.io",
                    first_name: null,
                    last_name: null,
                    __typename: "UserProfileData",
                },
                __typename: "User",
            },
            {
                id: "463",
                name: "user@warren.io",
                profile_data: {
                    created_at: "2025-06-19 11:24:16.930858",
                    email: "user@warren.io",
                    first_name: null,
                    last_name: null,
                    __typename: "UserProfileData",
                },
                __typename: "User",
            },
            {
                id: "462",
                name: "demo@warren.io",
                profile_data: {
                    created_at: "2025-03-12 14:24:40.463266",
                    email: "demo@warren.io",
                    first_name: "Demo",
                    last_name: "User",
                    __typename: "UserProfileData",
                },
                __typename: "User",
            },
        ],
        paging: regularPaging,
        __typename: "UserList",
    },
};

const generateVMData = (count: number) =>
    withGlobalSeed(349856, () =>
        times(count, (i) => {
            const id = i + 1;
            const multiplier = i % 3;

            const ba = pick(robin(getBillingAccountListQueryExample.admin_billing_account_list.items!, i), [
                "title",
                "email",
                "id",
                "running_totals",
                "__typename",
            ]);

            const { os_name, versions } = randomElement(vmImagesExample);
            const { os_version } = randomElement(versions);

            const created_at = randomPastTimestamp();
            const isDeleted = i % 7 === 0;
            const deleted_at = isDeleted
                ? Math.min(created_at + 1000 * 60 * 60 * 24 * id, Date.now() - 1000 * 60 * 60)
                : null;

            return {
                uuid: generateUuid(),
                service_uuid: null,
                hypervisor_uuid: defaultHostUuid,
                username: i % 2 ? "admin" : "user",
                boot_firmware: i % 2 ? BootFirmware.Uefi : null,
                is_designated_pool_visible: true,
                designated_pool_name: "Automatically added Pool",
                designated_pool_id: 1600000000 + id,
                designated_pool_uuid: extremeHostPoolUuid,
                is_current_pool_visible: true,
                current_pool_name: "Automatically added Pool",
                billing_account: Number(ba.id),
                billing_account_object: ba,
                name: `My VM ${id}`,
                description: `Description for custom-prefix-${id}`,
                memory: 1024 * (multiplier + 1),
                vcpu: 2 * (multiplier + 1),
                nic_iftune: null,
                status: isDeleted ? "deleted" : i % 5 ? "running" : "stopped",
                mac: randomMac(),
                public_ipv6: randomPublicIpv6(),
                private_ipv4: randomPrivateIpv4(),
                ip_address_object: {
                    address: randomPublicIpv4(),
                    _location: null,
                    __typename: "IpAddress" as const,
                },
                os_name,
                os_version,
                user_id,
                created_at,
                updated_at: created_at,
                deleted_at,
                _location: "tll",
                storage: [
                    {
                        created_at: randomPastTimestamp(),
                        name: "vda",
                        pool: "n/a",
                        primary: true,
                        shared: false,
                        size: 20 * (multiplier + 1),
                        type: "n/a",
                        user_id,
                        uuid: generateUuid(),
                        __typename: "GuestDisk" as const,
                    },
                ],
                __typename: "Vm" as const,
            };
        }),
    );

const exampleVms = generateVMData(15);
const [vm1, vm2] = exampleVms;

export const getVmListQueryExample: GetVmListQuery = {
    admin_vm_list: {
        items: exampleVms,
        paging: cursorPaging,
        __typename: "VmList",
    },
};

export const getServicePackageListQueryExample: GetServicePackageListQuery = {
    admin_servicepackage_list: {
        items: [
            {
                uuid: "e355b821-639d-4dbd-bd86-94964b585eaf",
                user_id,
                display_name: "Main Db",
                billing_account_id,
                is_multi_node: false,
                service: "mariadb",
                status: "active",
                version: "11.4",
                created_at: 1764588699000,
                updated_at: 1764588748000,
                is_deleted: false,
                resources: [
                    {
                        resource_id: "e96f08d9-90b3-43e9-b3e2-0a59f9706d9e",
                        resource_location: "tll",
                        resource_type: "vm",
                        __typename: "Entity",
                    },
                ],
                properties: {
                    service_ip: "10.61.20.19",
                    __typename: "ServicePackageProperties",
                },
                ip_address_object: {
                    address: "176.112.157.219",
                    __typename: "IpAddress",
                },
                whitelist_addresses: null,
                billing_account: billing_account_object,
                _location: "tll",
                __typename: "ServicePackage",
            },
            {
                uuid: "b374f080-c191-4874-8135-4b1796da59cd",
                user_id,
                display_name: "MariaDb",
                billing_account_id,
                is_multi_node: false,
                service: "mariadb",
                status: "deleted",
                version: "11.4",
                created_at: 1758226475000,
                updated_at: 1765568761000,
                is_deleted: true,
                resources: [
                    {
                        resource_id: "74924442-983c-48d4-a712-8bc0c86d9874",
                        resource_location: "tll",
                        resource_type: "vm",
                        __typename: "Entity",
                    },
                ],
                properties: {
                    service_ip: "10.109.35.199",
                    __typename: "ServicePackageProperties",
                },
                ip_address_object: null,
                whitelist_addresses: null,
                billing_account: {
                    title: "Demo Account",
                    email: "user@warren.io",
                    id: "130079",
                    __typename: "BillingAccount",
                },
                _location: "tll",
                __typename: "ServicePackage",
            },
            {
                uuid: "03a3eae1-c8ac-43fa-b442-5ebd207b2896",
                user_id,
                display_name: "PostgreSQL",
                billing_account_id,
                is_multi_node: true,
                service: "postgresql",
                status: "deleted",
                version: "16.4",
                created_at: 1757422106000,
                updated_at: 1757453135000,
                is_deleted: true,
                resources: [
                    {
                        resource_id: "af55e10b-7878-4d0e-8240-e508eabb6233",
                        resource_location: "tll",
                        resource_type: "virtual_ip",
                        __typename: "Entity",
                    },
                    {
                        resource_id: "a19f224d-0dc0-4ef5-bbe3-e167dec900d0",
                        resource_location: "tll",
                        resource_type: "vm",
                        __typename: "Entity",
                    },
                    {
                        resource_id: "887efc5c-1980-43dd-8440-45393451b63b",
                        resource_location: "tll",
                        resource_type: "vm",
                        __typename: "Entity",
                    },
                ],
                properties: {
                    service_ip: "10.109.35.146",
                    __typename: "ServicePackageProperties",
                },
                ip_address_object: null,
                whitelist_addresses: null,
                billing_account: billing_account_object,
                _location: "tll",
                __typename: "ServicePackage",
            },
        ],
        paging: regularPaging,
        __typename: "ServicePackageList",
    },
};

export const getBucketListQueryExample: GetBucketListQuery = {
    admin_bucket_list: {
        items: [
            {
                name: "my-bucket",
                user_id,
                size_bytes: 0,
                billing_account_id,
                num_objects: 0,
                created_at: 1764154998823,
                is_suspended: false,
                billing_account: billing_account_object,
                __typename: "Bucket",
            },
            {
                name: "test-bucket",
                user_id,
                size_bytes: 67154,
                billing_account_id,
                num_objects: 2,
                created_at: 1762451893483,
                is_suspended: false,
                billing_account: billing_account_object,
                __typename: "Bucket",
            },
            {
                name: "backup-storage",
                user_id,
                size_bytes: 209715200,
                billing_account_id,
                num_objects: 1,
                created_at: 1754562511028,
                is_suspended: true,
                billing_account: billing_account_object,
                __typename: "Bucket",
            },
        ],
        paging: regularPaging,
        __typename: "BucketList",
    },
};

export const getLoadBalancerListQueryExample: GetLoadBalancerListQuery = {
    admin_load_balancer_list: {
        items: [
            {
                uuid: "463f58a9-22ef-45a8-8aef-cbfbe3a1baf9",
                display_name: "LoadBalance",
                network_uuid: "c55ded40-1195-4146-b00a-f46c0b2ed57f",
                user_id,
                billing_account_id,
                billing_account: billing_account_object,
                created_at: 1759752248000,
                updated_at: 1759752248000,
                private_address: "10.3.119.248",
                forwarding_rules: [
                    {
                        uuid: "f3823fdf-564a-460d-827d-d238fbbd487f",
                        source_port: 80,
                        target_port: 80,
                        protocol: "TCP",
                        settings: {
                            connection_limit: 10000,
                            session_persistence: "SOURCE_IP",
                            __typename: "RuleSettings",
                        },
                        created_at: 1759752248000,
                        __typename: "Rule",
                    },
                ],
                targets: [
                    {
                        target_uuid: vm1.uuid,
                        created_at: 1759752248000,
                        target_type: "vm",
                        target_ip_address: vm1.private_ipv4,
                        __typename: "Target",
                    },
                ],
                _location: "tll",
                __typename: "LoadBalancer",
            },
            {
                uuid: "8febde18-636f-40fc-9809-107a98297e82",
                display_name: "Loader",
                network_uuid: "ef373a78-ffcc-4400-8a46-5199142d72ef",
                user_id,
                billing_account_id,
                billing_account: billing_account_object,
                created_at: 1756898384000,
                updated_at: 1756898384000,
                private_address: "10.123.8.8",
                forwarding_rules: [
                    {
                        uuid: "b6477f32-a0f9-46ad-8ca4-2c520dbd210a",
                        source_port: 8080,
                        target_port: 80,
                        protocol: "TCP",
                        settings: {
                            connection_limit: 10000,
                            session_persistence: "SOURCE_IP",
                            __typename: "RuleSettings",
                        },
                        created_at: 1756898384000,
                        __typename: "Rule",
                    },
                    {
                        uuid: "42168812-6392-4e02-8fef-e9256f888364",
                        source_port: 8088,
                        target_port: 80,
                        protocol: "TCP",
                        settings: {
                            connection_limit: 10000,
                            session_persistence: "SOURCE_IP",
                            __typename: "RuleSettings",
                        },
                        created_at: 1756898384000,
                        __typename: "Rule",
                    },
                ],
                targets: [
                    {
                        target_uuid: vm2.uuid,
                        created_at: 1756898384000,
                        target_type: "vm",
                        target_ip_address: vm2.private_ipv4,
                        __typename: "Target",
                    },
                ],
                _location: "tll",
                __typename: "LoadBalancer",
            },
        ],
        paging: cursorPaging,
        __typename: "LoadBalancerList",
    },
};

export const getIpAddressListQueryExample: GetIpAddressListQuery = {
    admin_ip_address_list: {
        items: [
            {
                uuid: "44e399f4-c217-420e-83b4-369ef72d59b7",
                user_id,
                billing_account_id,
                address: "176.112.157.232",
                is_ipv6: false,
                name: null,
                created_at: 1765533373000,
                updated_at: 1765533373000,
                assigned_to: vm1.uuid,
                assigned_to_resource_type: "virtual_machine",
                unassigned_at: null,
                _location: "tll",
                billing_account_object,
                __typename: "IpAddress",
            },
            {
                uuid: "20dcff0c-fdb6-42eb-869c-413ec409f323",
                user_id,
                billing_account_id,
                address: "176.112.157.231",
                is_ipv6: false,
                name: null,
                created_at: 1765532249000,
                updated_at: 1765532249000,
                assigned_to: vm2.uuid,
                assigned_to_resource_type: "virtual_machine",
                unassigned_at: null,
                _location: "tll",
                billing_account_object,
                __typename: "IpAddress",
            },
            {
                uuid: "a9e4ca04-5515-4454-bc91-231e422b7bc7",
                user_id,
                billing_account_id,
                address: "176.112.157.198",
                is_ipv6: false,
                name: null,
                created_at: 1764588747000,
                updated_at: 1764588747000,
                assigned_to: null,
                assigned_to_resource_type: null,
                unassigned_at: null,
                _location: "tll",
                billing_account_object,
                __typename: "IpAddress",
            },
        ],
        paging: cursorPaging,
        __typename: "IpAddressList",
    },
};

export const getInvoiceListQueryExample: GetInvoiceListQuery = {
    admin_invoice_list: {
        items: [
            {
                id: "3788300",
                padded_id: null,
                document_number: "#003788283",
                document_type: DocumentType.Invoice,
                period_start: 1765778530,
                period_end: 1765778530,
                created: 1765778530,
                status: 10,
                billing_account_id,
                due_date: 1766901730,
                vat_percentage: 20,
                discount_percentage: 0,
                type: "credit_topup",
                precalc_subtotal: 9,
                precalc_discount_amount: 0,
                precalc_credit: 0,
                precalc_total_before_tax: 9,
                precalc_vat_tax: 1.8,
                precalc_total: 10.8,
                billing_account: prepaymentBillingAccount,
                totals: {
                    subtotal_resource_usage: 0,
                    subtotal: 9,
                    discount_amount: 0,
                    credit: 0,
                    total_before_tax: 9,
                    vat_tax: 1.8,
                    total: 10.8,
                    __typename: "InvoiceTotals",
                },
                __typename: "Invoice",
            },
            {
                id: "3788216",
                padded_id: null,
                document_number: "CN-003",
                document_type: DocumentType.CreditNote,
                period_start: 1764618601,
                period_end: 1764618601,
                created: 1764618601,
                status: 5,
                billing_account_id,
                due_date: 1764618601,
                vat_percentage: 0,
                discount_percentage: 0,
                type: "credit_note",
                precalc_subtotal: 0.06,
                precalc_discount_amount: 0,
                precalc_credit: 0,
                precalc_total_before_tax: 0.06,
                precalc_vat_tax: 0.01,
                precalc_total: 0.07,
                billing_account: billing_account_object,
                totals: {
                    subtotal_resource_usage: 0,
                    subtotal: 0.06,
                    discount_amount: 0,
                    credit: 0,
                    total_before_tax: 0.06,
                    vat_tax: 0.01,
                    total: 0.07,
                    __typename: "InvoiceTotals",
                },
                __typename: "Invoice",
            },
            {
                id: "3788173",
                padded_id: null,
                document_number: "CN-002",
                document_type: DocumentType.CreditNote,
                period_start: 1763381792,
                period_end: 1763381792,
                created: 1763381792,
                status: 5,
                billing_account_id,
                due_date: 1763381792,
                vat_percentage: 0,
                discount_percentage: 0,
                type: "credit_note",
                precalc_subtotal: 1724.31,
                precalc_discount_amount: 0,
                precalc_credit: 0,
                precalc_total_before_tax: 1724.31,
                precalc_vat_tax: 120.66,
                precalc_total: 1844.97,
                billing_account: billing_account_object,
                totals: {
                    subtotal_resource_usage: 0,
                    subtotal: 1724.31,
                    discount_amount: 0,
                    credit: 0,
                    total_before_tax: 1724.31,
                    vat_tax: 120.66,
                    total: 1844.97,
                    __typename: "InvoiceTotals",
                },
                __typename: "Invoice",
            },
            {
                id: "3788221",
                padded_id: null,
                document_number: "#003788216",
                document_type: DocumentType.Invoice,
                period_start: 1761955200,
                period_end: 1764547199,
                created: 1764547199,
                status: 10,
                billing_account_id,
                due_date: 1765871100,
                vat_percentage: 20,
                discount_percentage: 15,
                type: "cloud_services",
                precalc_subtotal: 14.17,
                precalc_discount_amount: -2.13,
                precalc_credit: 0,
                precalc_total_before_tax: 12.04,
                precalc_vat_tax: 2.41,
                precalc_total: 14.45,
                billing_account: billing_account_object,
                totals: {
                    subtotal_resource_usage: 14.17,
                    subtotal: 14.17,
                    discount_amount: -2.13,
                    credit: 0,
                    total_before_tax: 12.04,
                    vat_tax: 2.41,
                    total: 14.45,
                    __typename: "InvoiceTotals",
                },
                __typename: "Invoice",
            },
            {
                id: "3788222",
                padded_id: null,
                document_number: "#003788217",
                document_type: DocumentType.Invoice,
                period_start: 1761955200,
                period_end: 1764547199,
                created: 1764547199,
                status: 5,
                billing_account_id,
                due_date: 1765871101,
                vat_percentage: 24,
                discount_percentage: 0,
                type: "cloud_services",
                precalc_subtotal: 1.98,
                precalc_discount_amount: 0,
                precalc_credit: 0,
                precalc_total_before_tax: 1.98,
                precalc_vat_tax: 0.48,
                precalc_total: 2.46,
                billing_account: billing_account_object,
                totals: {
                    subtotal_resource_usage: 1.98,
                    subtotal: 1.98,
                    discount_amount: 0,
                    credit: 0,
                    total_before_tax: 1.98,
                    vat_tax: 0.48,
                    total: 2.46,
                    __typename: "InvoiceTotals",
                },
                __typename: "Invoice",
            },
        ],
        paging: regularPaging,
        __typename: "InvoiceList",
    },
};
